import { useCallback, useState } from 'react';
import { useEditItem2 } from '../../../api/useNewItem';
import { apiFetch } from '../../../api/core';
import { useCRMContext } from '../CRMContext';
import { Contact } from '../types';

export interface BatchAttributeOp {
  attribute: string;
  operation: 'set' | 'append';
  value: any;
  type?: any;
}

// Match the shape expected by the Clojure backend, now including attributes and categories
export interface BatchUpdatePayload {
  _ids: string[];
  changes: Partial<Contact>;
  add_tags: number[];
  remove_tags: number[];
  add_categories: string[];
  remove_categories: string[];
  add_geo_country: string[];
  remove_geo_country: string[];
  add_geo_city: string[];
  remove_geo_city: string[];
  attributes: BatchAttributeOp[];
}

export const useContactsBatchUpdate = (onSuccess?: () => void) => {
  const ctx = useCRMContext();
  const apiPath = `${ctx.apiPath}/contact/batch-update`;
  
  const [targetIds, setTargetIds] = useState<string[]>([]);

  const editor = useEditItem2<BatchUpdatePayload>({
    save: async (item) => {
      if (!targetIds.length) throw new Error("No contacts selected for batch update");
      
      const payload: BatchUpdatePayload = {
        ...item,
        _ids: targetIds,
      };
      
      const response = await apiFetch<any>(apiPath, 'post', payload);
      
      if (onSuccess) {
        onSuccess();
      }
      
      return response;
    }
  });

  const open = useCallback((contactIds: string[]) => {
    setTargetIds(contactIds);
    
    // Initialize the form with empty arrays, empty changes map, and empty attributes
    editor.startEditing({
      _ids: [],
      changes: {},
      add_tags: [],
      remove_tags: [],
      add_categories: [],
      remove_categories: [],
      add_geo_country: [],
      remove_geo_country: [],
      add_geo_city: [],
      remove_geo_city: [],
      attributes: [], 
    });
  }, [editor]);

  const updateChangeField = useCallback((field: keyof Contact, value: any) => {
    if (!editor.item) return;
    editor.update({
      changes: {
        ...editor.item.changes,
        [field]: value
      }
    });
  }, [editor]);

  return {
    isOpen: editor.isEditing,
    targetIds,
    item: editor.item,
    dialogProps: editor.dialog,
    open,
    save: editor.save,
    update: editor.update,
    updateChangeField,
    isSaving: editor.isLoading,
    errors: editor.errors
  };
};

export type ContactsBatchUpdateData = ReturnType<typeof useContactsBatchUpdate>;